import { RejectionReasonService } from './../../../../services/rejection-reason/rejection-reason.service';
import { optionalName } from '../../../../validators/name-rules';
import { ChangeDetectionStrategy, Component, inject, signal, OnInit, computed, LOCALE_ID } from '@angular/core';
import { Router } from '@angular/router';
// import { $localize } from '@angular/localize/init';
import { FilterInput } from '../../../../shared/ui/shared-filters/filter-input/filter-input';
import { SubmitButton } from '../../../../shared/ui/Buttons/submit-button/submit-button';
import { ConfirmationDialog } from '../../../../shared/ui/confirmation-dialog/confirmation-dialog';
import { SuccessfulNotification } from '../../../../../../src/app/utils/successful-notification/successful-notification';

import {
  AbstractControl,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
export function arabicCharactersValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value as string;
    if (!value) {
      return null;
    }
    const arabicPattern = /^[\u0600-\u06FF]+(?:[ -][\u0600-\u06FF]+)*$/;
    return arabicPattern.test(value) ? null : { invalidArabicCharacters: true };
  };
}
export function englishCharactersValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value as string;
    if (!value) {
      return null;
    }
    const englishPattern = /^[A-Za-z]+(?:[ '-][A-Za-z]+)*$/;
    return englishPattern.test(value) ? null : { invalidEnglishCharacters: true };
  };
}
export function noLeadingOrTrailingSpacesValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value as string;
    if (!value) {
      return null;
    }
    return value !== value.trim() ? { leadingOrTrailingSpaces: true } : null;
  };
}
export function noNumbersValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value as string;
    if (!value) {
      return null;
    }
    return /\d/.test(value) ? { numbersNotAllowed: true } : null;
  };
}
@Component({
  selector: 'app-edit-rejection-reason',
  imports: [FilterInput, SubmitButton, ConfirmationDialog, SuccessfulNotification, ReactiveFormsModule],
  templateUrl: './edit-rejection-reason.html',
  styleUrl: './edit-rejection-reason.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EditRejectionReason implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private locale = inject(LOCALE_ID);
  showValidationErrors = signal(false);
  updatedReasonAR = signal('');
  updatedReasonEN = signal('');
  isArabic = computed(() => this.locale.startsWith('ar'));
  confirmEditReasonTitle = $localize`:@@adminRejectionReasonEdit:Edit Rejection Reason`;
  confirmEditReasonDescription = $localize`:@@confirmEditReasonDescription:Are you sure you want to save these changes?`;
  confirmButton = signal($localize`:@@cancellationReasonConfirm:Confirm`);

  Loading = $localize`:@@loading:Loading...`;
  cancelButton = $localize`:@@adminCategoryFormCancelButton:Cancel`;
  successEditTitle = computed(() =>
    this.isArabic()
      ? `تم تحديث سبب الرفض '${this.updatedReasonAR()}' بنجاح`
      : `Rejection reason '${this.updatedReasonEN()}' updated successfully`,
  );
  reasonENPlaceholder = $localize`:@@rejectionReasonEnglishNamePlaceholder:Enter name in English (optional)`;
  reasonARPlaceholder = $localize`:@@rejectionReasonArabicNamePlaceholder:Enter name in Arabic`;
  reasonArabicLabel = $localize`:@@rejectionReasonArabicLabel:Reason Title (Arabic)`;
  reasonEnglishLabel = $localize`:@@rejectionReasonEnglishLabel:Reason Title (English)`;
  rejectionReasonArabicRequired = $localize`:@@rejectionReasonArabicRequired:Reason Name in Arabic is required`;
  rejectionReasonMinLength = $localize`:@@rejectionReasonMinLength:Reason Name must be at least 3 characters`;
  rejectionReasonMaxLength = $localize`:@@rejectionReasonMaxLength:Reason Name cannot exceed 255 characters`;
  rejectionReasonArabicSpaces = $localize`:@@rejectionReasonArabicSpaces:Arabic Reason title cannot start or end with spaces`;
  rejectionReasonNumbersNotAllowed = $localize`:@@rejectionReasonNumbersNotAllowed:Reason Name cannot contain numbers`;
  rejectionReasonEnglishRequired = $localize`:@@rejectionReasonEnglishRequired:Reason Name in English is required`;
  rejectionReasonEnglishSpaces = $localize`:@@rejectionReasonArabicSpaces:English Reason title cannot start or end with spaces`;
  rejectionReasonArabicCharacters = $localize`:@@rejectionReasonArabicCharacters:Reason Name can contain Arabic letters, spaces, and hyphens only`;
  rejectionReasonEnglishCharacters = $localize`:@@rejectionReasonEnglishCharacters:Reason Name can contain English letters, spaces, hyphens, and apostrophes only`;
  reasonId = signal<number | null>(null);
  private rejectionReasonService = inject(RejectionReasonService);
  editReasonError = signal('');
  ngOnInit() {
    console.log('Edit page loaded');
    const id = this.route.snapshot.paramMap.get('reasonId');
    if (id) {
      this.reasonId.set(Number(id));
      this.loadReason();
    }
  }
  loadReason() {
    const id = this.reasonId();
    if (id === null) {
      console.log('No ID found');
      return;
    }
    this.rejectionReasonService
      .getAllRejectionReason(this.isArabic() ? 'ar' : 'en', {
        pageNum: 0,
        pageSize: 100,
      })
      .subscribe({
        next: (res) => {
          const reason = res.list.find((item) => item.id === id);
          if (!reason) {
            return;
          }
          this.form.patchValue({
            reasonAR: reason.titleAr,
            reasonEN: reason.titleEn,
          });
        },
        error: (error) => {
          console.error('GET ERROR:', error);
        },
      });
  }
  form = new FormGroup({
    reasonAR: new FormControl('', {
      nonNullable: true,
      validators: [
        Validators.required,
        Validators.minLength(3),
        Validators.maxLength(255),
        noLeadingOrTrailingSpacesValidator(),
        noNumbersValidator(),
        arabicCharactersValidator(),
      ],
    }),
    reasonEN: new FormControl('', {
      nonNullable: true,
      validators: [
        Validators.minLength(3),
        Validators.maxLength(255),
        noLeadingOrTrailingSpacesValidator(),
        noNumbersValidator(),
        englishCharactersValidator(),
      ],
    }),
  });
  showConfirmation = signal(false);
  showEditSuccess = signal(false);
  onArabicValueChange(value: string | number | null) {
    this.form.controls.reasonAR.setValue(typeof value === 'string' ? value : '');
  }
  onEnglishValueChange(value: string | number | null) {
    this.form.controls.reasonEN.setValue(typeof value === 'string' ? value : '');
  }
  onSubmit() {
    this.showValidationErrors.set(true);
    this.form.markAllAsTouched();
    if (this.form.invalid) {
      return;
    }
    this.showConfirmation.set(true);
  }
  onConfirmEdit() {
    this.editReasonError.set('');
    const reasonId = this.reasonId();
    this.confirmButton.set(this.Loading);
    if (reasonId === null) {
      this.confirmButton.set($localize`:@@adminCompaniesSubmitButton:Apply`);
      return;
    }
    const dto = {
      titleAr: this.form.controls.reasonAR.value,
      titleEn: optionalName(this.form.controls.reasonEN.value),
    };
    this.updatedReasonAR.set(dto.titleAr);
    this.updatedReasonEN.set(dto.titleEn || dto.titleAr);
    this.rejectionReasonService.updateRejectionReason(reasonId, dto).subscribe({
      next: (response) => {
        this.confirmButton.set($localize`:@@cancellationReasonConfirm:Confirm`);
        this.showConfirmation.set(false);
        this.showEditSuccess.set(true);
        setTimeout(() => {
          this.showEditSuccess.set(false);
          this.router.navigate(['/admin/rejection-reasons']);
        }, 3000);
      },
      error: (error) => {
        this.confirmButton.set($localize`:@@cancellationReasonConfirm:Confirm`);

        const apiError = error.error;

        this.editReasonError.set(
          this.isArabic()
            ? apiError?.messageAr || apiError?.messageEn || 'Something went wrong'
            : apiError?.messageEn || apiError?.messageAr || 'Something went wrong',
        );
      },
    });
  }

  goBack() {
    this.router.navigate(['/admin/rejection-reasons']);
  }
  closeConfirmation() {
    this.showConfirmation.set(false);
    this.editReasonError.set('');
  }
}
